import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useBusinessId } from '@/hooks/use-tenant-params';
import type { MeResolved, MeResponse } from '@/lib/types';

// `/api/me` is an IDENTITY endpoint: it answers for the signed-in user and
// never picks a tenant. When a screen wants the usage/entitlement view of one
// business it must NAME that business — and since T1.6B1 the name comes from
// the canonical route, which the server already proved for this request.
//
// What was here before, and why it is gone: this hook read the retired
// `pb.activeBusiness` key and, failing that, retried with "the first ACTIVE
// business in the user's list". Both are the shapes the tenancy model exists to
// remove — storage is not authorization, and a first-of-many pick is a silent
// choice that gets worse the moment someone owns two businesses. A screen with
// no business in its URL now simply gets the identity answer.
//
// A user with no businesses at all is `no-business`, not an error: they belong
// in /app/start.
type State =
  | { kind: 'loading' }
  | { kind: 'ready'; me: MeResolved }
  | { kind: 'no-business'; me: MeResponse }
  | { kind: 'error'; message: string };

let cache: MeResolved | null = null;
const subscribers = new Set<(s: State) => void>();

function resolved(me: MeResponse): MeResolved | null {
  return me.org && me.usage ? (me as MeResolved) : null;
}

let currentBusinessId: string | null = null;

async function refresh() {
  try {
    // The business is the one the URL named, or none. There is no fallback: a
    // screen outside a tenant route gets the identity answer, which is the
    // honest one.
    const url = currentBusinessId
      ? `/api/me?businessId=${encodeURIComponent(currentBusinessId)}`
      : '/api/me';
    const me = await api.get<MeResponse>(url);
    const ready = resolved(me);
    if (!ready) {
      cache = null;
      notify({ kind: 'no-business', me });
      return;
    }
    cache = ready;
    notify({ kind: 'ready', me: ready });
  } catch (err) {
    notify({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
  }
}

let lastKind: State['kind'] = 'loading';
function notify(s: State) {
  lastKind = s.kind;
  for (const fn of subscribers) fn(s);
}

export function useMe(): State {
  const businessId = useBusinessId() ?? null;
  const [state, setState] = useState<State>(
    cache ? { kind: 'ready', me: cache } : { kind: 'loading' },
  );
  useEffect(() => {
    subscribers.add(setState);
    // A different business than the cache was built for must be re-fetched:
    // reusing it would show one business's usage on another's screen.
    if (!cache || businessId !== currentBusinessId) {
      currentBusinessId = businessId;
      cache = null;
      refresh();
    }
    return () => {
      subscribers.delete(setState);
    };
  }, [businessId]);
  return state;
}

export function invalidateMe() {
  cache = null;
  // Retrying FROM an error must be visible: a second failure re-notifies the
  // same message, which changes no DOM text and re-announces nothing. Emitting
  // `loading` first makes the shell/route show its pending state, and a
  // repeat failure re-mounts the alert. Only from error — after a successful
  // save the callers expect a silent background refresh, not a flash.
  if (lastKind === 'error') notify({ kind: 'loading' });
  refresh();
}
